import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Chip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { Visibility } from "@mui/icons-material";
import apiClient from "../utils/apiClient";
import { toast } from "react-toastify";

const ReviewList = () => {
  const [reviews, setReviews] = useState([]);
  const [search, setSearch] = useState("");
  const [approveFilter, setApproveFilter] = useState("");
  const [replyFilter, setReplyFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [replyText, setReplyText] = useState("");

  const approvalColor = {
    pending: "warning",
    approved: "success",
    rejected: "error",
  };

  // 🔥 Fetch Reviews
  const fetchReviews = async () => {
    try {
      setLoading(true);

      const res = await apiClient.get("/api/vendor/reviews", {
        params: {
          search,
          approvalStatus: approveFilter,
          isReply: replyFilter,
          page: page + 1,
          limit: rowsPerPage,
        },
      });

      setReviews(res?.data?.data?.data || []);
      setTotalCount(res?.data?.data?.total || 0);
    } catch (err) {
      toast.error("Failed to load reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [search, approveFilter, replyFilter, page, rowsPerPage]);

  // 🔥 Open Dialog
  const handleView = (row) => {
    setSelectedReview(row);
    setReplyText(row.vendorReply || "");
    setOpenDialog(true);
  };

  // 🔥 Submit Reply
  const handleReplySubmit = async () => {
    if(replyText){
      try {
        await apiClient.put(`/api/vendor/reviews/${selectedReview.id}`, {
          vendorReply: replyText,
        });
  
        toast.success("Reply submitted successfully");
        setOpenDialog(false);
        fetchReviews();
      } catch (err) {
        toast.error("Failed to submit reply");
      }
    }else{
     toast.error("Reply text is required");

    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5">Review List</Typography>
      </Box>

      {/* 🔍 Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <TextField
          label="Search"
          size="small"
          value={search}
          onChange={(e) => {
            setPage(0);
            setSearch(e.target.value);
          }}
        />

        <TextField
          select
          size="small"
          label="Approval"
          value={approveFilter}
          onChange={(e) => {
            setPage(0);
            setApproveFilter(e.target.value);
          }}
          sx={{ width: 180 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="rejected">Rejected</MenuItem>
        </TextField>

        <TextField
          select
          size="small"
          label="Reply Status"
          value={replyFilter}
          onChange={(e) => {
            setPage(0);
            setReplyFilter(e.target.value);
          }}
          sx={{ width: 180 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="true">Replied</MenuItem>
          <MenuItem value="false">Not Replied</MenuItem>
        </TextField>
      </Box>

      {/* 📋 Table */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Rating</TableCell>
              <TableCell>Approval</TableCell>
              <TableCell>Reply</TableCell>
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {reviews.length > 0 ? (
              reviews.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.product?.name}</TableCell>
                  <TableCell>{row?.customer?.name}</TableCell>

                  <TableCell>
                    <Chip label={`⭐ ${row.rating}`} size="small" />
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={row.approvalStatus}
                      size="small"
                      color={approvalColor[row.approvalStatus]}
                    />
                  </TableCell>

                  <TableCell>
                    {row.isReply ? (
                      <Chip label="Replied" size="small" color="success" />
                    ) : (
                      <Chip label="Pending" size="small" color="warning" />
                    )}
                  </TableCell>

                  <TableCell align="center">
                    <IconButton onClick={() => handleView(row)}>
                      <Visibility />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {loading ? "Loading..." : "No reviews found"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 📄 Pagination */}
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />

      {/* 🔥 Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth>
        <DialogTitle>Review Details</DialogTitle>

        <DialogContent dividers>
          {selectedReview && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography>
                <b>Product:</b> {selectedReview?.product?.name}
              </Typography>

              <Typography>
                <b>Customer:</b> {selectedReview?.customer?.name}
              </Typography>

              <Typography>
                <b>Rating:</b> ⭐ {selectedReview.rating}
              </Typography>

              <Typography>
                <b>Review:</b> {selectedReview.comment}
              </Typography>

              <TextField
                label="Vendor Reply"
                multiline
                rows={4}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                fullWidth
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleReplySubmit}>
            Submit Reply
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ReviewList;